import React, { useState, useEffect } from 'react';
import { QUOTE_ROTATION_INTERVAL } from '../constants';
import './FullscreenInspiration.css';
import { useTranslation, translateList } from '../i18n';
import { formatDate } from '../utils/format';
import Icon from './Icon';

interface FullscreenInspirationProps {
  isOpen: boolean;
  onClose: () => void;
  quotes?: string[]; // Optional custom quotes override
}

const FullscreenInspiration: React.FC<FullscreenInspirationProps> = ({
  isOpen,
  onClose,
  quotes: propQuotes
}) => {
  const { language, t } = useTranslation();

  const defaultQuotes = translateList(language, 'inspiration.quotes');
  const quotes = propQuotes && propQuotes.length > 0 ? propQuotes : defaultQuotes;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Change quote every minute when fullscreen is open
  useEffect(() => {
    if (!isOpen) return;

    const quoteTimer = setInterval(() => {
      setCurrentQuoteIndex(prev => (prev + 1) % quotes.length);
    }, QUOTE_ROTATION_INTERVAL);

    return () => clearInterval(quoteTimer);
  }, [isOpen, quotes.length]);

  // Reset quote index on language change to start from the first localized item
  useEffect(() => {
    setCurrentQuoteIndex(0);
  }, [language]);

  // Prevent body scroll when fullscreen is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [isOpen]);

  const formatClock = (date: Date) =>
    formatDate(date, language, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const formatLongDate = (date: Date) =>
    formatDate(date, language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (!isOpen) return null;

  return (
    <div className="fullscreen-modal" onClick={onClose}>
      <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
        <button 
          className="close-fullscreen-btn"
          onClick={onClose}
          title={t('common.close')}
          aria-label={t('common.close')}
        >
          <Icon name="close" />
        </button>
        
        <div className="fullscreen-center">
          <div className="time-display">
            <div className="current-time">{formatClock(currentTime)}</div>
            <div className="current-date">{formatLongDate(currentTime)}</div>
          </div>
          
          <div className="quote-section">
            <div className="inspirational-quote">"{quotes[currentQuoteIndex]}"</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullscreenInspiration;
